import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Scissors, MailCheck } from "lucide-react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function RegisterSuccessPage() {
  const q = useQuery();
  const email = q.get("email") || "";
  const delivery = q.get("delivery") || "";
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const resend = async () => {
    if (!email) return;
    setLoading(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/auth/verify-email/request", { email });
      setMsg(data.already_verified
        ? "Your email is already verified — you can sign in."
        : "Verification email sent. Please check your inbox.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#FAFAFA] text-[#0A0A0A]" data-testid="register-success-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Scissors size={20} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          <div className="font-serif text-xl tracking-widest">MMS HUB CENTER</div>
        </div>

        <div className="border border-[#E5E5E5] p-8 bg-[#FFFFFF]">
          <MailCheck size={28} className="text-[#0A0A0A] mb-4" strokeWidth={1.5}/>
          <div className="label-caps text-[#0A0A0A] mb-3">Almost there</div>
          <h2 className="font-serif text-3xl font-light mb-4">Check your email</h2>
          <p className="text-sm text-[#525252] leading-relaxed mb-6">
            We&#39;ve sent a verification link{email ? <> to <span className="text-[#0A0A0A]" data-testid="register-success-email">{email}</span></> : null}.
            Please click the link in the email to activate your account. The link expires in 24 hours.
          </p>

          {delivery === "pending" && (
            <div className="text-xs text-[#171717] border border-[#E5E5E5] p-3 mb-4" data-testid="register-success-pending">
              Email delivery is pending — the Resend integration hasn&#39;t been configured yet.
              Ask a Super Admin to set <code>RESEND_API_KEY</code> and <code>SENDER_EMAIL</code>, or configure a provider from Settings → Email Provider.
            </div>
          )}

          {msg && <div className="text-xs text-green-400 mb-4" data-testid="register-success-msg">{msg}</div>}
          {err && <div className="text-xs text-red-600 mb-4" data-testid="register-success-err">{err}</div>}

          <button
            onClick={resend}
            disabled={loading || !email}
            data-testid="register-success-resend"
            className="btn-gold w-full disabled:opacity-50"
          >
            {loading ? "Sending…" : "Resend verification email"}
          </button>

          <div className="text-center text-xs text-[#737373] mt-6">
            <Link to="/login" className="text-[#0A0A0A] hover:text-[#171717]" data-testid="register-success-login-link">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
